import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';
import { createAuth } from '../Helpers';
import moment from 'moment';

export const reducerName = 'resourceState';

export const getAllUnits = createAsyncThunk(
    'resourceState/getAllUnits',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/unit`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const getAllBookings = createAsyncThunk(
    'resourceState/getAllBookings',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/unitbooking`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const createBooking = createAsyncThunk(
    'resourceState/createBooking',
    async ({ unitId, teamId, personId, startTime, endTime }, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/unitbooking`,
                data: {
                    unitId,
                    teamId,
                    personId,
                    startTime: moment(startTime).format('YYYY-MM-DD HH:mm'),
                    endTime: moment(endTime).format('YYYY-MM-DD HH:mm'),
                },
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            if (error.response?.data === 'double_booking') {
                return rejectWithValue({ doubleBook: true });
            }
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const editBooking = createAsyncThunk(
    'resourceState/editBooking',
    async ({ id, startTime, endTime }, { getState, rejectWithValue }) => {
        try {
            const data = {
                id,
                startTime: moment(startTime).format('YYYY-MM-DD HH:mm'),
                endTime: moment(endTime).format('YYYY-MM-DD HH:mm'),
            };
            const url = `${config.server}/api/unitbooking/edit/bookingid/${id}`;
            const response = await axios({
                method: 'PUT',
                data,
                url,
                withCredentials: true,
                auth: createAuth(getState),
            });
            // Attach person from personState if needed
            const persons = getState().personState?.persons || {};
            const person = persons[response.data.personId];
            return { ...response.data, person };
        } catch (error) {
            if (error.response?.data === 'double_booking') {
                return rejectWithValue({ doubleBook: true });
            }
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const deleteBooking = createAsyncThunk(
    'resourceState/deleteBooking',
    async (id, { getState, rejectWithValue }) => {
        try {
            const url = `${config.server}/api/unitbooking/bookingid/${id}`;
            const response = await axios({
                method: 'DELETE',
                url,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

const initialState = {
    units: [],
    bookings: [],
    createBooking: {
        unitId: '',
        teamId: '',
        personId: null,
        startTime: moment().valueOf(),
        endTime: moment().valueOf(),
    },
    doubleBook: false,
    unitMenuId: false,
    showTeamMenu: false,
    showTimeMenu: false,
    showUnitBookingMenuBox: false,
    showEditBox: false,
    showDeleteBox: false,
    bookingIdToUnitBookingMenu: null,
    bookingIdToEdit: null,
    bookingIdToDelete: null,
    loading: { getUnits: false, getBookings: false },
    
    activeCategory: undefined,
    showCategories: true,
    categories: [],
};

const resourceSlice = createSlice({
    name: reducerName,
    initialState,
    reducers: {
        setBookingField: (state, action) => {
            state.createBooking[action.payload.field] = action.payload.value;
        },
        setTeamAndBook: (state, action) => {
            const { teamId, personId } = action.payload;
            state.createBooking.teamId = teamId;
            state.createBooking.personId = personId;
        },
        unitMenu: (state, action) => {
            state.unitMenuId = action.payload.visible ? action.payload.unitId : false;
        },
        showTeamMenu: (state, action) => {
            state.showTeamMenu = action.payload;
        },
        showTimeMenu: (state, action) => {
            state.showTimeMenu = action.payload;
            state.doubleBook = false;
        },
        toggleShowUnitBookingMenuBox: (state, action) => {
            if (state.showEditBox) {
                state.bookingIdToUnitBookingMenu = null;
                state.showEditBox = false;
                // showUnitBookingMenuBox remains unchanged
            } else {
                const show = !state.showUnitBookingMenuBox;
                let id = null;
                if (show && action.payload) {
                    id = action.payload.id;
                }
                state.showUnitBookingMenuBox = show;
                state.bookingIdToUnitBookingMenu = id;
            }
        },
        toggleShowEditBox: (state, action) => {
            const show = !state.showEditBox;
            let id = null;
            if (show && action.payload !== undefined) {
                id = action.payload.id;
            }
            state.showEditBox = show;
            state.bookingIdToEdit = id;
        },
        toggleShowDeleteBox: (state, action) => {
            const show = !state.showDeleteBox;
            let id = null;
            if (show) {
                id = action.payload.id;
            }
            state.showDeleteBox = show;
            state.bookingIdToDelete = id;
        },
        toggleShowCategories: (state, action) => {
            if (action.payload) {
                state.showCategories = action.payload;
                state.units = state.allUnits;
            } else {
                state.showCategories = action.payload;
            }
        },
        setActiveCategory: (state, action) => {
            state.activeCategory = action.payload;
        },
        listActiveCategoryUnits: (state, action) => {
            state.allUnits = state.units;
            state.units = state.units.filter(
                u =>
                    u.category === state.activeCategory ||
                    (state.activeCategory === "Andre" && u.category == null)
            );
        },
    },
    extraReducers: (builder) => {
        // getAllUnits
        builder
            .addCase(getAllUnits.pending, (state) => {
                state.loading.getUnits = true;
                state.error = null;
            })
            .addCase(getAllUnits.fulfilled, (state, action) => {
                state.loading.getUnits = false;
                state.units = action.payload;
                state.categories = [...new Set(action.payload.map(u => u.category || "Andre"))];
            })
            .addCase(getAllUnits.rejected, (state, action) => {
                state.loading.getUnits = false;
                state.error = action.payload || 'An error occurred';
            });

        // getAllBookings
        builder
            .addCase(getAllBookings.pending, (state) => {
                state.loading.getBookings = true;
                state.error = null;
            })
            .addCase(getAllBookings.fulfilled, (state, action) => {
                state.loading.getBookings = false;
                state.bookings = action.payload;
            })
            .addCase(getAllBookings.rejected, (state, action) => {
                state.loading.getBookings = false;
                state.error = action.payload || 'An error occurred';
            });

        // createBooking
        builder
            .addCase(createBooking.pending, (state) => {
                state.loading.createBooking = true;
                state.error = null;
            })
            .addCase(createBooking.fulfilled, (state, action) => {
                state.loading.createBooking = false;
                state.bookings.push(action.payload);
                
                state.showTeamMenu = false;
                state.unitMenuId = false;
                state.bookings = [
                    ...state.bookings,
                    action.payload
                ];
            })
            .addCase(createBooking.rejected, (state, action) => {
                state.loading.createBooking = false;
                if (action.payload?.doubleBook) {
                    state.doubleBook = true;
                } else {
                    state.error = action.payload || 'An error occurred';
                }
            });
        //editBooking
        builder
            .addCase(editBooking.pending, (state) => {
                state.doubleBook = false;
            })
            .addCase(editBooking.fulfilled, (state, action) => {
                const unitBooking = action.payload;
                const unit = state.units.find(u => u.id === unitBooking.unitId);
                unitBooking.unit = unit;
                state.bookingIdToEdit = null;
                state.showEditBox = false;
                state.showTimeMenu = false;
                state.bookings = state.bookings.map(booking =>
                    booking.id === unitBooking.id ? { ...booking, ...unitBooking } : booking
                );
                state.createBooking = unitBooking;
            })
            .addCase(editBooking.rejected, (state, action) => {
                if (action.payload?.doubleBook) {
                    state.doubleBook = true;
                    state.showTimeMenu = true;
                } else {
                    state.error = action.payload || 'An error occurred';
                }
            });
        // deleteBooking
        builder
            .addCase(deleteBooking.pending, (state) => {
                state.loading.deleteBooking = true;
                state.error = null;
            })
            .addCase(deleteBooking.fulfilled, (state, action) => {
                state.loading.deleteBooking = false;

                const id = action.payload.id;
                state.bookings = state.bookings.filter(booking => booking.id !== id);
                state.showDeleteBox = false;
                state.bookingIdToDelete = null;
            })
            .addCase(deleteBooking.rejected, (state, action) => {
                state.loading.deleteBooking = false;
                state.error = action.payload || 'An error occurred';
            });
    },
});

export const { 
    setBookingField, 
    setTeamAndBook, 
    unitMenu, 
    showTeamMenu, 
    showTimeMenu, 
    toggleShowUnitBookingMenuBox,
    toggleShowEditBox, 
    toggleShowDeleteBox, 
    toggleShowCategories,
    setActiveCategory,
    listActiveCategoryUnits
} = resourceSlice.actions;

export const toggleShowEditBoxThunk = (p) => (dispatch) => {
    const show = true;
    dispatch(toggleShowEditBox(p));
    dispatch(toggleShowUnitBookingMenuBox(p));
    dispatch(showTimeMenu(show));
};

export const toggleShowDeleteBoxThunk = (p) => (dispatch) => {
    dispatch(toggleShowDeleteBox(p));
    dispatch(toggleShowUnitBookingMenuBox(p));
};

export const setBookingFieldThunk = (field, value, booking) => (dispatch, getState) => {
    dispatch(setBookingField({ field, value }));

    if (booking !== undefined && field === "startTime") {
        dispatch(setBookingField({ field: "endTime", value: booking.endTime }));
    } else if (booking !== undefined && field === "endTime") {
        dispatch(setBookingField({ field: "startTime", value: booking.startTime }));
    }

    // Print the new value after update
    const updatedValue = getState().resourceState.createBooking[field];
    console.log(`Updated ${field}:`, updatedValue);
};

export const setToCategoryView = () => async (dispatch, getState) => {
    dispatch(resourceSlice.actions.toggleShowCategories(true));
    dispatch(resourceSlice.actions.setActiveCategory(undefined));
};

export const setToUnitView = (category) => async (dispatch, getState) => {
    dispatch(toggleShowCategories(false));
    dispatch(setActiveCategory(category));
    dispatch(listActiveCategoryUnits(category));
};

export default resourceSlice.reducer;